import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PieChartComponent } from '../_shared/components';
import { DateFilterComponent } from '../../_shared/components';
import {
  AccountFilterData,
  DateIntervalFilterData,
  DriverFilterData,
  SummaryRequest,
  WidgetFilterData,
} from '../../_shared/models';
import { DateInterval } from '../../_shared/enums';
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { FilterService } from '../../_shared/services';
import { FuelEfficiencyWidgetComponent } from './components/widgets/fuel-efficiency-widget/fuel-efficiency-widget.component';
import { AccountFilterComponent } from '../../_shared/components';
import { TotalDistanceWidgetComponent } from './components/widgets/total-distance-widget/total-distance-widget.component';
import { TotalTripsWidgetComponent } from './components/widgets/total-trips-widget/total-trips-widget.component';
import { TotalFuelUsedWidgetComponent } from './components/widgets/total-fuel-used-widget/total-fuel-used-widget.component';
import { CommonModule } from '@angular/common';
import { FuelConsumptionService } from './services/fuel-consumption.service';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../_shared/services/error-handler.service';
import {
  ConsumptionStats,
  FuelConsumptionDetails,
} from './models/fuel-consumption-details.model';
import { PaginationComponent } from '../../_shared/components/pagination/pagination.component';
import { AccountDateFilterRequest } from '../../_shared/models/accountDateFilter.model';
import { GraphTrendIconComponent } from '../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { transformValue } from '../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { FuelConsumptionWidgetGraphComponent } from './components/widgets/fuel-consumption-widget-graph/fuel-consumption-widget-graph.component';
import { EmptyStateComponent } from '../../_shared/components/states/empty-state/empty-state.component';
import { BasePageComponent } from '../../_shared/components/page/base-page.component';
import { buildDateFilterFromPageFilter } from '../../_shared/helpers/page-filter-state-helper';
import { MakeAndModelFilterComponent } from '../_shared/components/filters/make-model-filter/make-model-filter.component';
import { SearchComponent } from '../../_shared/components/filters/search/search.component';
import { FuelConsumptionRequest } from '../_shared/models/fuel-consumption-request.model';
import { DriverFilterComponent } from '../../_shared/components/filters/driver-filter/driver-filter.component';
import { MakeModelFilterData } from '../_shared/models/make-model.model';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-consumption',
  standalone: true,
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    CommonModule,
    AccountFilterComponent,
    PieChartComponent,
    DateFilterComponent,
    FuelEfficiencyWidgetComponent,
    TotalDistanceWidgetComponent,
    TotalTripsWidgetComponent,
    TotalFuelUsedWidgetComponent,
    PaginationComponent,
    FuelConsumptionWidgetGraphComponent,
    GraphTrendIconComponent,
    EmptyStateComponent,
    MakeAndModelFilterComponent,
    SearchComponent,
    DriverFilterComponent,
  ],
  templateUrl: './fuel-consumption.component.html',
  styleUrl: './fuel-consumption.component.scss',
})
export class FuelConsumptionComponent
  extends BasePageComponent
  implements OnInit, OnDestroy {
  private filterService = inject(FilterService);
  private fuelConsumptionService = inject(FuelConsumptionService);
  private errorService = inject(ErrorHandlerService);

  fuelConsumptionDetails: FuelConsumptionDetails[] = [];
  accountFilter: AccountFilterData = {} as AccountFilterData;
  makeModelFilter: MakeModelFilterData = { makesAndModels: [] };
  driverFilter: DriverFilterData;
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  page: number = 1;
  pageSize: number = 8;
  totalPages: number = 0;
  totalCount: number = 0;
  highConsumptionStats: ConsumptionStats[] = [];
  selectedFilter: string = 'Drivers';
  consumptionTotalValue: number = 0;
  consumptionPercentageChange: any;
  searchTerm: string = '';

  isDetailsLoading: boolean = true;
  isSummaryLoading: boolean = true;
  isHighConsumptionLoading: boolean = true;

  private detailsCancelSubject: Subject<void> = new Subject<void>();
  private summaryCancelSubject: Subject<void> = new Subject<void>();
  private highConsumptionCancelSubject: Subject<void> = new Subject<void>();
  private accountFilterReady: boolean = false;
  accountFilterChanged$ = new Subject<number[]>();

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.detailsCancelSubject.next();
    this.detailsCancelSubject.complete();

    this.summaryCancelSubject.next();
    this.summaryCancelSubject.complete();

    this.highConsumptionCancelSubject.next();
    this.highConsumptionCancelSubject.complete();
  }

  protected override getFilters() {
    return {
      ...this.dateFilter,
      ...this.accountFilter,
    };
  }

  protected override applyPageFilterState(pageFilter: any): void {
    // Apply date filter
    this.dateFilter = buildDateFilterFromPageFilter(
      this.dateFilter,
      pageFilter
    );

    // Apply account filter
    this.accountFilter = {
      accountIds: pageFilter.accountIds,
    } as AccountFilterData;
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;

    // Only trigger update after account filter has loaded user-accounts API
    // The accountFilter may be set from cached state, but that doesn't mean user-accounts API has completed
    if (this.accountFilterReady) {
      this.updateFilter();
    }
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;

    // Reset dependent filters when account changes
    this.makeModelFilter = { makesAndModels: [] };
    this.driverFilter = { driverIds: [] };

    // Emit account IDs so make-model and driver filters can load their data
    this.accountFilterChanged$.next(this.accountFilter.accountIds ?? []);

    this.accountFilterReady = true;
    this.updateFilter();
  }

  onMakeModelFilterChange(filter: MakeModelFilterData): void {
    this.makeModelFilter = filter;
    this.page = 1;
    this.getFuelConsumptionDetails();
  }

  onSearchTermChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.getFuelConsumptionDetails();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter(this.getFilters() as WidgetFilterData);
      this.updatePageFilterState();
      this.page = 1;
      this.getFuelConsumptionDetails();
      this.getHighConsumptionSummary();
      this.getHighConsumptionDrivers();
    }
  }

  onDriverFilterChange(filter: DriverFilterData): void {
    this.driverFilter = filter;
    this.page = 1;
    this.getFuelConsumptionDetails();
  }

  async getFuelConsumptionDetails(): Promise<void> {
    this.isDetailsLoading = true;

    try {
      const request: FuelConsumptionRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.page,
        pageSize: this.pageSize,
        makesAndModels: this.makeModelFilter?.makesAndModels ?? [],
        searchTerm: this.searchTerm,
        driverIds: this.driverFilter?.driverIds ?? [],
      };

      this.detailsCancelSubject.next();
      const response =
        await this.fuelConsumptionService.getFuelConsumptionDetails(
          request,
          this.detailsCancelSubject
        );
      this.totalPages = response.data?.totalPages ?? 0;
      this.totalCount = response.data?.totalCount ?? 0;
      this.fuelConsumptionDetails =
        response.data?.results.map((item) => {
          return {
            assetName: item.assetName,
            driver: `${item.firstName} ${item.lastName}`,
            distanceTraveled: item.distance,
            fuelUsed: item.fuelUsed,
            efficiency: item.efficiency,
            timeStamp: item.timeStamp,
          };
        }) ?? [];
      this.isDetailsLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isDetailsLoading = false;
        },
      });
    }
  }

  async getHighConsumptionDrivers() {
    await this.getHighConsumptionData('Drivers');
  }

  async getHighConsumptionAssets() {
    await this.getHighConsumptionData('Assets');
  }

  async getHighConsumptionSummary() {
    this.isSummaryLoading = true;

    try {
      const request: SummaryRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        previousTimeFrom: this.dateFilter.previousCycleStartDate!,
        previousTimeTo: this.dateFilter.previousCycleEndDate!,
      };

      this.summaryCancelSubject.next();
      const response =
        await this.fuelConsumptionService.getHighConsumptionSummary(
          request,
          this.summaryCancelSubject
        );
      this.consumptionTotalValue = response.data?.totalValue ?? 0;
      this.consumptionPercentageChange = response.data?.percentageChange;
      this.isSummaryLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isSummaryLoading = false;
        },
      });
    }
  }

  private async getHighConsumptionData(
    type: 'Drivers' | 'Assets'
  ): Promise<void> {
    this.isHighConsumptionLoading = true;
    this.selectedFilter = type;

    try {
      const request: AccountDateFilterRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
      };

      this.highConsumptionCancelSubject.next();
      const response =
        type === 'Drivers'
          ? await this.fuelConsumptionService.getHighConsumptionDrivers(
            request,
            this.highConsumptionCancelSubject
          )
          : await this.fuelConsumptionService.getHighConsumptionAssets(
            request,
            this.highConsumptionCancelSubject
          );

      this.highConsumptionStats = response.data ?? [];
      this.isHighConsumptionLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isHighConsumptionLoading = false;
        },
      });
    }
  }

  formatValue(value: number): string {
    return `${transformValue(value)} km/ℓ`;
  }

  formatPercentChange(): string {
    if (this.consumptionPercentageChange == null) {
      return 'N/A';
    }

    const sign = this.consumptionPercentageChange! > 0 ? '+' : '';
    return `${sign}${this.consumptionPercentageChange!}%`;
  }

  get showingCount(): number {
    if (this.totalCount === 0) return 0;

    const end = Math.min(this.page * this.pageSize, this.totalCount);
    return end;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.page = page;
    this.getFuelConsumptionDetails();
  }

  protected buttonMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  get showLastRowBorder(): boolean {
    return (
      !this.isDetailsLoading &&
      this.fuelConsumptionDetails?.length > 0 &&
      this.fuelConsumptionDetails.length < this.pageSize
    );
  }

  protected onRefresh(): void {
    this.updateFilter();
  }

  protected override shouldRefresh(): boolean {
    return (
      !isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)
    );
  }
}
